import { DevService } from "@gap/backend/app-services/dev/DevService"
import { ValidEmailSchema } from "@gap/backend/util/validations/validation-email"
import { GridColDef, GridEventListener } from "@mui/x-data-grid"
import { apiClient } from "api/trpc/trpc-client"
import Loader from "reusable/mobx-helpers/Loader"
import ModalState from "reusable/mobx-helpers/ModalState"
import { formHandler, FormHandlerConfig } from "reusable/mobx-store-utils/form-handlers/new/form-handler"
import MoreMenu, { MoreMenuOption } from "reusable/more-menu/MoreMenu"
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { CalendarMonth } from "@mui/icons-material"
import Send from "@mui/icons-material/Send"
import Check from "@mui/icons-material/Check"
import Close from "@mui/icons-material/Close"
import { snackbarError, snackbarSuccess } from "reusable/mui/snackbar/snackbar"
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { themeConfig } from "@gap/shared/theme"

type IssueId = 'NoInvitedAcceptedOn' | 'NoInviteSent' | 'Deactivated' | 'DuplicatedUser'

export default class UserManagementStore {
   userLoader = new Loader()
   users: DevService.searchUserByEmail[] | undefined
   selectedUser: undefined | DevService.searchUserByEmail
   userOrgModalState = new ModalState()
   fields = {
      email: ''
   }
   issues: { issue: string, id: IssueId }[] = []

   constructor() { }

   get formHandler() {
      return formHandler(this.fields, {
         email: { type: 'string' },
      } satisfies FormHandlerConfig)
   }

   get userTableColumns(): GridColDef<DevService.searchUserByEmail>[] {
      return [
         {
            field: 'email',
            headerName: 'User',
            minWidth: 320,
            renderCell: ({ row }) => <Box sx={{ my: 1 }}>
               <Typography variant="body1">{row.email}</Typography>
               <Typography variant="body2" color={themeConfig.primary[600]}>{row.firstName} {row.lastName}</Typography>
               <Typography variant="body2" color={themeConfig.primary[600]}>{row.id}</Typography>
            </Box >,
            valueGetter: (_value, row) => `${row.email} ${row.firstName} ${row.lastName}`,
         },
         {
            field: 'isGuest',
            headerName: 'Is guest',
            renderCell: ({ row }) => row.isGuest ? 'Yes' : 'No'
         },
         {
            field: 'inviteAcceptedOn',
            headerName: 'Invite accepted on',
            renderCell: ({ row }) => row.inviteAcceptedOn ?
               <Check sx={{ color: 'success.main' }} /> :
               <Close sx={{ color: 'error.main' }} />
         },
         {
            field: 'lastLogin',
            headerName: 'Last login',
            renderCell: ({ row }) => {
               if (!row.lastLogin) {
                  return 'Unknown'
               }
               const date = new Date(row.lastLogin)
               return date.toLocaleString()
            }
         },
         {
            field: 'menu',
            headerName: '',
            sortable: false,
            align: 'right',
            maxWidth: 40,
            renderCell: ({ row }) => (
               <MoreMenu
                  width="250px"
                  icon={<MoreVertIcon />}
                  id={row.id}
                  options={this.getUserTableMoreMenuOptions(row)}
               />
            )
         }
      ]
   }

   get selectedUserTableColumns(): GridColDef<DevService.searchUserByEmailOrg>[] {
      return [
         {
            field: 'id',
            headerName: 'Org info',
            minWidth: 320,
            renderCell: ({ row }) => <Box sx={{ my: 1 }}>
               <Typography variant="body1">{row.orgName}</Typography>
               <Typography variant="body2" color={themeConfig.primary[600]}>{row.id}</Typography>
               <Typography variant="body2" color={themeConfig.primary[600]}>{row.orgType}</Typography>
            </Box >,
            valueGetter: (_value, row) => `${row.orgName} ${row.id} ${row.orgType}`,
         },
         {
            field: 'roleName',
            headerName: 'Role name',
         },
         {
            field: 'deactivated',
            headerName: 'Is deactivated',
            renderCell: ({ row }) => row.deactivated ?
               <Close sx={{ color: 'error.main' }} /> :
               <Check sx={{ color: 'success.main' }} />
         },
         {
            field: 'inviteSent',
            headerName: 'Has invite sent set',
            renderCell: ({ row }) => row.inviteSent ?
               <Check sx={{ color: 'success.main' }} /> :
               <Close sx={{ color: 'error.main' }} />
         },
         {
            field: 'menu',
            headerName: '',
            sortable: false,
            align: 'right',
            maxWidth: 40,
            renderCell: ({ row }) => (
               <MoreMenu
                  width="250px"
                  icon={<MoreVertIcon />}
                  id={row.id}
                  options={this.getUserOrgTableMoreMenuOptions(row)}
               />
            )
         }
      ]
   }

   getUserTableMoreMenuOptions(user: DevService.searchUserByEmail): MoreMenuOption[] {
      const options: MoreMenuOption[] = []

      if (!user.inviteAcceptedOn) {
         options.push({
            title: 'Set invite accepted on',
            callback: async () => this.setInviteAcceptedOn(user),
            icon: CalendarMonth
         })
      }
      options.push({
         title: 'Send password reset email',
         icon: Send,
         callback: async () => this.sendPasswordResetEmail(user),
      })

      return options
   }

   getUserOrgTableMoreMenuOptions(userOrg: DevService.searchUserByEmailOrg): MoreMenuOption[] {
      const options: MoreMenuOption[] = []

      if (!userOrg.inviteSent) {
         options.push({
            title: 'Set invite sent on',
            callback: async () => this.setInviteSentOn(userOrg),
            icon: CalendarMonth
         })
      }

      if (userOrg.deactivated) {
         options.push({
            title: 'Activate user org',
            callback: async () => this.activateUserOrg(userOrg),
            icon: Send,
         })
      } else {
         options.push({
            title: 'Deactivate user org',
            callback: async () => this.deactivateUserOrg(userOrg),
            icon: Send,
         })
      }

      return options
   }

   setInviteSentOn = async (userOrg: DevService.searchUserByEmailOrg) => {
      await this.userLoader.executeLoading(async () => {
         await apiClient.user.setInviteSentOn.mutate({ userId: userOrg.userId, orgId: userOrg.id })
         userOrg.inviteSent = true
         this.issues = this.issues.filter(issue => !(issue.issue.includes(userOrg.id) && issue.issue.includes(userOrg.userId) && issue.id === 'NoInviteSent'))
         snackbarSuccess('Invite sent on set')
      })
   }

   activateUserOrg = async (userOrg: DevService.searchUserByEmailOrg) => {
      await this.userLoader.executeLoading(async () => {
         await apiClient.user.updateUserOrgActivationStatus.mutate({ userId: userOrg.userId, orgId: userOrg.id, isActive: true })
         userOrg.deactivated = false
         this.issues = this.issues.filter(issue => !(issue.issue.includes(userOrg.id) && issue.issue.includes(userOrg.userId) && issue.id === 'Deactivated'))
         snackbarSuccess('User org activated')
      })
   }

   deactivateUserOrg = async (userOrg: DevService.searchUserByEmailOrg) => {
      await this.userLoader.executeLoading(async () => {
         await apiClient.user.updateUserOrgActivationStatus.mutate({ userId: userOrg.userId, orgId: userOrg.id, isActive: false })
         userOrg.deactivated = true
         this.issues.push({ issue: `DeactivatedOn set on UserOrgEntity for user ${userOrg.userId} and org ${userOrg.id}. This will prevent them from accessing the org.`, id: 'Deactivated' })
         snackbarSuccess('User org deactivated')
      })
   }

   sendPasswordResetEmail = async (user: DevService.searchUserByEmail) => {
      await this.userLoader.executeLoading(async () => {
         if (!user.inviteAcceptedOn) {
            snackbarError('User has not accepted invite. Do that before sending a password reset email')
            return
         }
         await apiClient.user.sendPasswordResetEmail.mutate({ email: user.email })
         snackbarSuccess('Password reset email sent')
      })
   }

   setInviteAcceptedOn = async (user: DevService.searchUserByEmail) => {
      await this.userLoader.executeLoading(async () => {
         await apiClient.user.setInviteAcceptedOn.mutate({ userId: user.id })
         this.users = this.users!.map(u => {
            if (u.id !== user.id) return u
            return { ...u, inviteAcceptedOn: u.lastUpdated || new Date().toISOString() }
         })
         this.issues = this.issues.filter(issue => !(issue.issue.includes(user.id) && issue.id === 'NoInvitedAcceptedOn'))
         snackbarSuccess('Invite accepted on set')
      })
   }

   search = async () => {
      if (this.formHandler.validate(ValidEmailSchema)) {
         await this.userLoader.executeLoading(async () => {
            this.selectedUser = undefined
            this.users = await apiClient.user.searchUsers.query({ email: this.fields.email })
            this.issues = []

            if (this.users.length > 1) {
               this.issues.push({ issue: `Found ${this.users.length} users with the same email. This is an issue. Contact dev team`, id: 'DuplicatedUser' })
            }
            for (const user of this.users || []) {
               if (!user.inviteAcceptedOn) {
                  this.issues.push({ issue: `No invite accepted on set on UserEntity with id ${user.id}. This may prevent them loggin in.`, id: 'NoInvitedAcceptedOn' })
               }

               for (const userOrg of user.orgs || []) {
                  if (!userOrg.inviteSent) {
                     this.issues.push({ issue: `No inviteSentOn set on UserOrgEntity for user ${user.id} and org ${userOrg.id}. This may prevent them from accessing the org.`, id: 'NoInviteSent' })
                  }
                  if (userOrg.deactivated) {
                     this.issues.push({ issue: `DeactivatedOn set on UserOrgEntity for user ${user.id} and org ${userOrg.id}. This will prevent them from accessing the org.`, id: 'Deactivated' })
                  }
               }
            }
         })
      }
   }

   onUserCellClick: GridEventListener<"cellClick"> = (p) => {
      if (p.field !== 'menu') {
         this.selectedUser = p.row
         this.userOrgModalState.open()
      }
   }
}
